import { useEffect, useMemo, useState } from 'react';
import { Link } from 'wouter';
import { adminApi, type AdminTeamMember } from '@/lib/adminApi';
import { ADMIN_AREAS, ROLE_LABELS, STAFF_ROLES } from '@/lib/adminAreas';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Crown, KeyRound, MessageSquarePlus, Shield, Users } from 'lucide-react';

function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';
}

function Person({
  member,
  isMe,
  tone,
  action,
}: {
  member: AdminTeamMember;
  isMe: boolean;
  tone: 'owner' | 'director' | 'staff';
  action?: React.ReactNode;
}) {
  const ring = tone === 'owner'
    ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
    : tone === 'director'
      ? 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30'
      : 'bg-gray-700/50 text-gray-300 border-gray-600';
  return (
    <div className="flex items-center gap-3 rounded-lg border border-gray-800 bg-gray-900/60 px-4 py-3">
      <div className={`w-10 h-10 rounded-full border flex items-center justify-center text-sm font-semibold shrink-0 ${ring}`}>
        {initials(member.display_name)}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-white truncate">
          {member.display_name}
          {isMe && <span className="ml-2 text-[10px] uppercase tracking-wider text-gray-500">You</span>}
        </p>
        <p className="text-xs text-gray-400">
          {ROLE_LABELS[member.role] ?? member.role}
          {member.status === 'invited' && <span className="text-gray-500"> · not signed in yet</span>}
        </p>
      </div>
      {action}
    </div>
  );
}

/**
 * Who runs H-Plate and who to ask. Shows the owner, the directors and the
 * staff by role. Contact goes through Requests so every decision is on record;
 * no email addresses are shown here.
 */
export default function Team() {
  const { user, isManager, role, areas } = useAdminAuth();
  const [members, setMembers] = useState<AdminTeamMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.getAdminTeam()
      .then(({ members: list }) => setMembers(list.filter((m) => m.status === 'active' || m.status === 'invited')))
      .catch((err: any) => toast.error(err.message || 'Could not load the team'))
      .finally(() => setLoading(false));
  }, []);

  const owner = members.filter((m) => m.role === 'owner');
  const directors = members.filter((m) => m.role === 'director');
  const staffByRole = useMemo(() => STAFF_ROLES.map((r) => ({
    role: r,
    people: members.filter((m) => m.role === r.key),
  })).filter((g) => g.people.length > 0), [members]);

  const myAreas = ADMIN_AREAS.filter((a) => areas.includes(a.key));
  const askHref = (to?: string) => `/admin/requests?new=1${to ? `&to=${encodeURIComponent(to)}` : ''}`;

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Team</h1>
          <p className="text-sm text-gray-400 mt-1">
            Who runs H-Plate and who to ask. For approvals, decisions or questions, send a request to the directors.
          </p>
        </div>
        <Link href={askHref()}>
          <Button className="bg-emerald-600 hover:bg-emerald-500 text-white shrink-0">
            <MessageSquarePlus className="w-4 h-4 mr-2" /> Ask the directors
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 bg-gray-800/50" />)}
        </div>
      ) : (
        <>
          <Card className="bg-gray-900/50 border-gray-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <Crown className="w-4 h-4 text-amber-400" /> Leadership
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <p className="text-[11px] uppercase tracking-wider text-gray-500">Owner</p>
                {owner.map((m) => (
                  <Person key={m.user_id} member={m} isMe={m.user_id === user?.id} tone="owner" />
                ))}
              </div>
              <div className="space-y-2">
                <p className="text-[11px] uppercase tracking-wider text-gray-500">Directors</p>
                <p className="text-xs text-gray-500">
                  The directors run the company day to day. They approve deals and spending, decide on anything
                  outside your role, and change who can open what.
                </p>
                {directors.length === 0 ? (
                  <p className="text-sm text-gray-400 rounded-lg border border-dashed border-gray-700 px-4 py-3">
                    No directors have been added yet. Requests still reach the owner in the meantime.
                  </p>
                ) : (
                  <div className="grid gap-2 sm:grid-cols-2">
                    {directors.map((m) => (
                      <Person
                        key={m.user_id}
                        member={m}
                        isMe={m.user_id === user?.id}
                        tone="director"
                        action={m.user_id !== user?.id && m.status === 'active' ? (
                          <Link href={askHref(m.user_id)}>
                            <Button size="sm" variant="outline" className="border-gray-700 text-gray-200 hover:text-white" aria-label={`Send a request to ${m.display_name}`}>
                              Ask
                            </Button>
                          </Link>
                        ) : undefined}
                      />
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/50 border-gray-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <Users className="w-4 h-4 text-cyan-400" /> Staff
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {staffByRole.length === 0 ? (
                <p className="text-sm text-gray-500">No staff accounts yet.</p>
              ) : staffByRole.map(({ role: r, people }) => (
                <div key={r.key} className="space-y-2">
                  <div>
                    <p className="text-sm font-medium text-gray-200">{r.label}</p>
                    <p className="text-xs text-gray-500">{r.summary}</p>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {people.map((m) => (
                      <Person key={m.user_id} member={m} isMe={m.user_id === user?.id} tone="staff" />
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {!isManager && (
            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <Shield className="w-4 h-4 text-emerald-400" /> Your access
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-gray-400">
                  You are signed in as {ROLE_LABELS[role ?? ''] ?? 'staff'}. Along with the Overview, Team, Requests and
                  Settings, you can open:
                </p>
                {myAreas.length === 0 ? (
                  <p className="text-sm text-gray-500">No work areas yet.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {myAreas.map((a) => (
                      <Badge key={a.key} variant="outline" className="border-gray-700 text-gray-300" title={a.pages}>
                        {a.label}
                      </Badge>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  Revenue, invoices, deal prices and the audit log stay with the owner and directors.
                </p>
                <Link href="/admin/requests?new=1&kind=access&subject=Access%20request">
                  <Button size="sm" variant="outline" className="border-gray-700 text-gray-200 hover:text-white">
                    <KeyRound className="w-3.5 h-3.5 mr-2" /> Ask for more access
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
